import { ref, reactive, computed } from '@vue/composition-api'
import nominationsApi from '@/services/api/nominations'
import EventBus from '@/store/EventBus'
import useFormAlert from '@/composables/useFormAlert'
import useUserRole from '@/composables/useUserRole'

export default function useNominationAdd(draftId) {
  const { isSecretaryMember, isSecretaryOrAdmin } = useUserRole()

  const draft = ref(null)
  const loading = ref(false)
  const loadFailed = ref(false)

  const draftEvents = ref([])

  const memberSearchResults = ref([])
  const loadingMembers = ref(false)
  const selectedMember = ref(null)

  const byEvent = reactive({
    eventId: null,
    selectedEvent: null,
    horses: [],
    loadingHorses: false,
    maxHorses: null,
    selectedHorseIds: []
  })

  const byHorse = reactive({
    horses: [],
    loadingHorses: false,
    horseId: null,
    selectedHorse: null,
    events: [],
    loadingEvents: false,
    selectedEventId: null
  })

  const bySubmission = reactive({
    events: [],
    loadingEvents: false,
    eventId: null,
    selectedEvent: null,
    quantity: 1,
    maxSubmissions: null,
    used: null,
    loadingQuantity: false
  })

  const submitting = ref(false)
  const alert = useFormAlert()

  const userNominations = ref([])
  const loadingNominations = ref(false)

  // IDs selected for bulk scratch
  const scratchSelectedIds = ref([])

  const needsToBeProcessed = computed(() =>
    userNominations.value.some(nomination => nomination.needs_to_be_processed === true))

  async function loadUserNominations(userId, silent = false) {
    // A secretary-member always sees their own nominations here, even while
    // browsing other members' horses/events to nominate on their behalf.
    const effectiveUserId = isSecretaryMember ? null : userId

    if (!silent) loadingNominations.value = true
    try {
      const params = effectiveUserId != null ? { user_id: effectiveUserId } : {}
      const res = await nominationsApi.userNominations(draftId, params)
      userNominations.value = res.data.data != null ? res.data.data : []
    } catch (_) {
      // silent — list is non-critical
    } finally {
      if (!silent) loadingNominations.value = false
    }
  }

  async function init() {
    loading.value = true
    try {
      const draftRes = await nominationsApi.draft(draftId)
      draft.value = draftRes.data.data

      const eventsRes = await nominationsApi.draftEvents(draftId)
      const allEvts = eventsRes.data.data.events != null ? eventsRes.data.data.events : []
      draftEvents.value = allEvts

      const subEventsRes = await nominationsApi.submissionEventsForDraft(draftId)
      bySubmission.events = subEventsRes.data.data.events != null ? subEventsRes.data.data.events : []
    } catch (err) {
      loadFailed.value = true
      const data = err.response != null ? err.response.data : null
      const msg = data != null && data.message != null
        ? data.message
        : 'Failed to load draft data. Please try again.'
      EventBus.$emit('http-error-persistent', msg)
    } finally {
      loading.value = false
    }

    loadUserNominations(null)
  }

  let searchTimer = null

  // The "choose the competitor to nominate for" select has two audiences:
  // - Secretary / Admin nominate on behalf of anyone -> GET /v2/nominations/members-search
  //   (whole financial roster; empty term = first page, typed term = name search).
  // - Regular members / volunteers nominate only within their own family -> GET /v2/nominations/members
  //   (loaded once; a typed term just filters that small list client-side, no request).
  function searchMembers(query) {
    const term = (query || '').trim()
    clearTimeout(searchTimer)

    if (isSecretaryOrAdmin) {
      if (term && term.length < 2) return // backend requires q >= 2 chars
      searchTimer = setTimeout(async () => {
        loadingMembers.value = true
        try {
          const res = await nominationsApi.membersSearch(term ? { q: term } : {})
          memberSearchResults.value = res.data.data
        } catch (_err) {
          // global interceptor handles display
        } finally {
          loadingMembers.value = false
        }
      }, 300)
      return
    }

    if (memberSearchResults.value.length > 0) return // family list already loaded; multiselect filters it

    searchTimer = setTimeout(async () => {
      loadingMembers.value = true
      try {
        const res = await nominationsApi.members({})
        memberSearchResults.value = res.data.data
      } catch (_err) {
        // global interceptor handles display
      } finally {
        loadingMembers.value = false
      }
    }, 300)
  }

  async function loadHorsesForMember(memberId) {
    byHorse.loadingHorses = true
    try {
      const res = await nominationsApi.memberHorses(memberId)
      byHorse.horses = res.data.data.horses != null ? res.data.data.horses : []
    } catch (_err) {
      // silent
    } finally {
      byHorse.loadingHorses = false
    }
  }

  async function setByEventEvent(eventId) {
    byEvent.eventId = eventId != null && eventId !== '' ? Number(eventId) : null
    byEvent.horses = []
    byEvent.selectedHorseIds = []
    byEvent.maxHorses = null

    if (selectedMember.value == null || byEvent.eventId == null) return

    byEvent.loadingHorses = true
    try {
      const res = await nominationsApi.memberHorses(selectedMember.value.id, { event_id: byEvent.eventId })
      byEvent.horses = res.data.data.horses != null ? res.data.data.horses : []
      byEvent.maxHorses = res.data.data.max_horses != null ? res.data.data.max_horses : null
    } catch (_err) {
      // silent
    } finally {
      byEvent.loadingHorses = false
    }
  }

  async function setBySubmissionEvent(eventId) {
    bySubmission.eventId = eventId != null && eventId !== '' ? Number(eventId) : null
    bySubmission.maxSubmissions = null
    bySubmission.used = null
    bySubmission.quantity = 1

    if (selectedMember.value == null || bySubmission.eventId == null) return

    bySubmission.loadingQuantity = true
    try {
      const res = await nominationsApi.submissionQuantityForMember(selectedMember.value.id, { event_id: bySubmission.eventId })
      bySubmission.maxSubmissions = res.data.data.max_submissions != null ? res.data.data.max_submissions : null
      bySubmission.used = res.data.data.used != null ? res.data.data.used : 0
    } catch (_err) {
      // silent
    } finally {
      bySubmission.loadingQuantity = false
    }
  }

  function setMember(member) {
    selectedMember.value = member

    byEvent.horses = []
    byEvent.selectedHorseIds = []
    byEvent.maxHorses = null
    byHorse.horseId = null
    byHorse.selectedHorse = null
    byHorse.events = []
    bySubmission.maxSubmissions = null
    bySubmission.used = null
    bySubmission.quantity = 1

    const userId = member != null ? member.id : null
    loadUserNominations(userId, true)

    if (member == null) {
      byHorse.horses = []
      return
    }

    if (byEvent.eventId != null) {
      setByEventEvent(byEvent.eventId)
    }

    if (bySubmission.eventId != null) {
      setBySubmissionEvent(bySubmission.eventId)
    }

    loadHorsesForMember(member.id)
  }

  function toggleHorse(horseId) {
    const idx = byEvent.selectedHorseIds.indexOf(horseId)
    if (idx === -1) {
      byEvent.selectedHorseIds = [...byEvent.selectedHorseIds, horseId]
    } else {
      byEvent.selectedHorseIds = byEvent.selectedHorseIds.filter(id => id !== horseId)
    }
  }

  async function setByHorseHorse(horseId) {
    byHorse.horseId = horseId != null && horseId !== '' ? Number(horseId) : null
    byHorse.events = []
    byHorse.selectedEventId = null

    if (byHorse.horseId == null) return

    byHorse.loadingEvents = true
    try {
      const res = await nominationsApi.horseEvents(byHorse.horseId, { draft_id: draftId })
      const allEvts = res.data.data.events != null ? res.data.data.events : []
      byHorse.events = allEvts
    } catch (_err) {
      // silent
    } finally {
      byHorse.loadingEvents = false
    }
  }

  function extractError(err) {
    const data = err.response != null ? err.response.data : null
    return data != null && data.message != null
      ? data.message
      : 'A server error occurred. Please try again later.'
  }

  async function submit(activeTab) {
    if (selectedMember.value == null) return false
    alert.clear()

    if (activeTab === 'submission') {
      submitting.value = true
      try {
        const res = await nominationsApi.storeSubmission({
          draft_id: Number(draftId),
          user_id: selectedMember.value.id,
          event_id: bySubmission.eventId,
          quantity: bySubmission.quantity
        })

        await loadUserNominations(selectedMember.value.id, true)
        if (bySubmission.eventId != null) await setBySubmissionEvent(bySubmission.eventId)

        const msg = res.data.message != null ? res.data.message : 'Nomination saved successfully.'
        EventBus.$emit('show-success', msg)
        return true
      } catch (err) {
        alert.showError(extractError(err))
        return false
      } finally {
        submitting.value = false
      }
    }

    const eventId = activeTab === 'event' ? byEvent.eventId : byHorse.selectedEventId
    const horseIds = activeTab === 'event' ? byEvent.selectedHorseIds : [byHorse.horseId]

    submitting.value = true
    try {
      const res = await nominationsApi.create({
        draft_id: Number(draftId),
        user_id: selectedMember.value.id,
        event_id: eventId,
        horse_ids: horseIds
      })

      if (activeTab === 'event') {
        byEvent.selectedHorseIds = []
      } else {
        byHorse.selectedEventId = null
      }

      await loadUserNominations(selectedMember.value.id, true)

      const msg = res.data.message != null ? res.data.message : 'Nomination saved successfully.'
      EventBus.$emit('show-success', msg)
      return true
    } catch (err) {
      alert.showError(extractError(err))
      return false
    } finally {
      submitting.value = false
    }
  }

  async function deleteNomination(nomId) {
    const res = await nominationsApi.deleteNomination(nomId)
    const userId = selectedMember.value != null ? selectedMember.value.id : null
    await loadUserNominations(userId, true)
    return res
  }

  async function bulkScratch() {
    if (scratchSelectedIds.value.length === 0) return null
    const res = await nominationsApi.bulkScratch({ nomination_ids: scratchSelectedIds.value })
    scratchSelectedIds.value = []
    const userId = selectedMember.value != null ? selectedMember.value.id : null
    await loadUserNominations(userId, true)
    return res
  }

  async function updateNominationRider(nomId, userMemberId) {
    try {
      await nominationsApi.updateNomination(nomId, { user_member_id: userMemberId })
      const userId = selectedMember.value != null ? selectedMember.value.id : null
      await loadUserNominations(userId, true)
    } catch (err) {
      EventBus.$emit('show-error', extractError(err))
    }
  }

  async function updateNominationHorse(nomId, horseId) {
    try {
      await nominationsApi.updateNomination(nomId, { horse_id: horseId })
      const userId = selectedMember.value != null ? selectedMember.value.id : null
      await loadUserNominations(userId, true)
    } catch (err) {
      EventBus.$emit('show-error', extractError(err))
    }
  }

  async function reorderNominations(items) {
    return nominationsApi.reorderNominations({ items })
  }

  function toggleScratchSelect(nomId) {
    const idx = scratchSelectedIds.value.indexOf(nomId)
    if (idx === -1) {
      scratchSelectedIds.value = [...scratchSelectedIds.value, nomId]
    } else {
      scratchSelectedIds.value = scratchSelectedIds.value.filter(id => id !== nomId)
    }
  }

  function toggleSelectAll(allIds) {
    if (scratchSelectedIds.value.length === allIds.length) {
      scratchSelectedIds.value = []
    } else {
      scratchSelectedIds.value = [...allIds]
    }
  }

  return {
    draft,
    loading,
    loadFailed,
    draftEvents,
    memberSearchResults,
    loadingMembers,
    selectedMember,
    byEvent,
    byHorse,
    bySubmission,
    submitting,
    alert,
    userNominations,
    loadingNominations,
    scratchSelectedIds,
    needsToBeProcessed,
    init,
    searchMembers,
    setMember,
    setByEventEvent,
    toggleHorse,
    setByHorseHorse,
    setBySubmissionEvent,
    submit,
    deleteNomination,
    bulkScratch,
    updateNominationRider,
    updateNominationHorse,
    reorderNominations,
    toggleScratchSelect,
    toggleSelectAll,
    loadUserNominations
  }
}



// WEBPACK FOOTER //
// ./src/composables/nominations/useNominationAdd.js